import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import './AdminApps.css'; // Reusing the same CSS for consistency
import { Link } from 'react-router-dom';

function AdminRoles() {
  const { currentUser, isSuperAdmin } = useAuth();
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedRole, setSelectedRole] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: {}
  });
  const [roleUsers, setRoleUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [modifyingUsers, setModifyingUsers] = useState({});

  // Fetch all roles and users on component mount
  useEffect(() => {
    if (isSuperAdmin) {
      fetchRoles();
      fetchUsers();
    }
  }, [isSuperAdmin]);

  // Fetch all roles
  const fetchRoles = async () => {
    try {
      setLoading(true);
      const response = await apiService.get('/roles');
      console.log('Roles response:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        // New API format
        setRoles(response.data);
        setError(null);
      } else if (response && response.roles) {
        // Format from documentation
        setRoles(response.roles);
        setError(null);
      } else if (response && Array.isArray(response)) {
        // Simple array format
        setRoles(response);
        setError(null);
      } else {
        console.error('Unexpected roles response format:', response);
        setError('Failed to load roles: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to fetch roles:', err);
      setError(`Failed to load roles: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all users
  const fetchUsers = async () => {
    try {
      const response = await apiService.get('/users');
      console.log('Users response in AdminRoles:', response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setUsers(response.data);
      } else if (response && response.users) {
        // Fallback for backward compatibility
        setUsers(response.users);
      } else {
        console.error('Unexpected users response format:', response);
      }
    } catch (err) {
      console.error('Failed to fetch users:', err);
      // Don't set error state here to avoid disrupting the main role management flow
    }
  };

  // Fetch users for a specific role
  const fetchRoleUsers = async (roleId) => {
    try {
      setLoadingUsers(true);
      const response = await apiService.get(`/roles/${roleId}/users`);
      console.log(`Role users response for role ${roleId}:`, response);
      
      if (response && response.success && Array.isArray(response.data)) {
        setRoleUsers(response.data);
      } else if (response && response.users) {
        // Format from documentation
        setRoleUsers(response.users);
      } else {
        console.error('Unexpected role users response format:', response);
        setRoleUsers([]);
      }
    } catch (err) {
      console.error('Failed to fetch role users:', err);
      setRoleUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle permission checkbox changes
  const handlePermissionChange = (category, action, checked) => {
    setFormData(prevData => {
      const updatedPermissions = { ...prevData.permissions };
      
      if (!updatedPermissions[category]) {
        updatedPermissions[category] = [];
      }
      
      if (checked) {
        // Add permission if not already included
        if (!updatedPermissions[category].includes(action)) {
          updatedPermissions[category] = [...updatedPermissions[category], action];
        }
      } else {
        // Remove permission
        updatedPermissions[category] = updatedPermissions[category].filter(a => a !== action);
        
        // Remove category if empty
        if (updatedPermissions[category].length === 0) {
          delete updatedPermissions[category];
        }
      }
      
      return {
        ...prevData,
        permissions: updatedPermissions
      };
    });
  };

  // Select a role to edit
  const handleSelectRole = async (role) => {
    setSelectedRole(role);
    setFormMode('edit');
    setFormData({
      name: role.name,
      description: role.description || '',
      permissions: role.permissions || {}
    });
    
    // Reset search terms and modifying states
    setUserSearchTerm('');
    setModifyingUsers({});
    
    // Fetch role's users
    await fetchRoleUsers(role.id);
  };

  // Reset form for creating a new role
  const handleNewRole = () => {
    setSelectedRole(null);
    setFormMode('create');
    setFormData({
      name: '',
      description: '',
      permissions: {}
    });
    setRoleUsers([]);
    setUserSearchTerm('');
    setModifyingUsers({});
  };

  // Submit form to create or update a role
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      
      if (formMode === 'create') {
        // Create new role
        const response = await apiService.post('/roles', formData);
        console.log('Create role response:', response);
        
        if (response && response.success && response.data) {
          setError(null);
          handleNewRole(); // Reset form
          fetchRoles(); // Refresh role list
        } else if (response && response.role) {
          // Fallback for backward compatibility
          setError(null);
          handleNewRole(); // Reset form
          fetchRoles(); // Refresh role list
        } else {
          console.error('Unexpected create role response:', response);
          setError('Failed to create role: Unexpected response format');
        }
      } else {
        // Update existing role
        const response = await apiService.put(`/roles/${selectedRole.id}`, formData);
        console.log('Update role response:', response);
        
        if (response && response.success && response.data) {
          setError(null);
          fetchRoles(); // Refresh role list
        } else if (response && response.role) {
          // Fallback for backward compatibility
          setError(null);
          fetchRoles(); // Refresh role list
        } else {
          console.error('Unexpected update role response:', response);
          setError('Failed to update role: Unexpected response format');
        }
      }
    } catch (err) {
      console.error('Failed to save role:', err);
      setError(`Failed to save role: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Delete a role
  const handleDeleteRole = async () => {
    if (!selectedRole) return;
    
    if (!window.confirm(`Are you sure you want to delete the role "${selectedRole.name}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      const response = await apiService.delete(`/roles/${selectedRole.id}`);
      console.log('Delete role response:', response);
      
      if (response && response.success) {
        setError(null);
        handleNewRole(); // Reset form
        fetchRoles(); // Refresh role list
      } else if (response && response.message) {
        // Fallback for backward compatibility
        setError(null);
        handleNewRole(); // Reset form
        fetchRoles(); // Refresh role list
      } else {
        console.error('Unexpected delete role response:', response);
        setError('Failed to delete role: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to delete role:', err);
      setError(`Failed to delete role: ${err.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  // Assign a user to a role
  const handleAssignUser = async (userId) => {
    if (!selectedRole) return;
    
    try {
      setModifyingUsers({ [userId]: true });
      const response = await apiService.post('/roles/assign', {
        userId: userId,
        roleId: selectedRole.id
      });
      console.log('Assign user to role response:', response);
      
      if (response && (response.success || response.message)) {
        fetchRoleUsers(selectedRole.id); // Refresh role users
      } else {
        console.error('Unexpected assign user response:', response);
        setError('Failed to assign user to role: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to assign user to role:', err);
      setError(`Failed to assign user to role: ${err.message || 'Unknown error'}`);
    } finally {
      setModifyingUsers({ [userId]: false });
    }
  };

  // Remove a user from a role
  const handleRemoveUser = async (userId) => {
    if (!selectedRole) return;
    
    try {
      setModifyingUsers({ [userId]: true });
      const response = await apiService.delete(`/roles/user/${userId}/role/${selectedRole.id}`);
      console.log('Remove user from role response:', response);
      
      if (response && (response.success || response.message)) {
        fetchRoleUsers(selectedRole.id); // Refresh role users
      } else {
        console.error('Unexpected remove user response:', response);
        setError('Failed to remove user from role: Unexpected response format');
      }
    } catch (err) {
      console.error('Failed to remove user from role:', err);
      setError(`Failed to remove user from role: ${err.message || 'Unknown error'}`);
    } finally {
      setModifyingUsers({ [userId]: false });
    }
  };

  // Filter roles based on search term
  const filteredRoles = roles.filter(role => 
    role.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    role.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Check if a user has the selected role
  const hasRole = (userId) => {
    return roleUsers.some(user => user.id === userId);
  };

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    // First check if the user already has the role
    if (hasRole(user.id)) return false;
    
    // Then check if the user matches the search term
    return (
      user.username?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      (user.first_name && user.first_name.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
      (user.last_name && user.last_name.toLowerCase().includes(userSearchTerm.toLowerCase()))
    );
  });

  // Permission categories and actions
  const permissionCategories = [
    { 
      name: 'users', 
      label: 'Users',
      actions: ['create', 'read', 'update', 'delete']
    },
    { 
      name: 'roles', 
      label: 'Roles',
      actions: ['create', 'read', 'update', 'delete']
    },
    { 
      name: 'apps', 
      label: 'Apps',
      actions: ['create', 'read', 'update', 'delete']
    },
    { 
      name: 'content', 
      label: 'Content',
      actions: ['create', 'read', 'update', 'delete']
    }
  ];

  // If user is not a superadmin, show access denied message
  if (!isSuperAdmin) {
    return (
      <div className="admin-container">
        <h1>Access Denied</h1>
        <p>You do not have permission to access this page. This area is restricted to superadmins only.</p>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <h1>Role Management</h1>
      
      {/* Admin navigation */}
      <div className="admin-nav mb-4">
        <ul className="nav nav-tabs">
          <li className="nav-item">
            <Link to="/admin" className="nav-link">Dashboard</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/apps" className="nav-link">Apps</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/users" className="nav-link">Users</Link>
          </li>
          <li className="nav-item">
            <Link to="/admin/roles" className="nav-link active">Roles</Link>
          </li>
        </ul>
      </div>
      
      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}
      
      <div className="admin-layout">
        {/* Left side - Role list */}
        <div className="admin-sidebar">
          <div className="admin-search">
            <input
              type="text"
              placeholder="Search roles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          
          <button 
            className="btn btn-primary mb-3 w-100"
            onClick={handleNewRole}
          >
            Create New Role
          </button>
          
          {loading && <p>Loading roles...</p>}
          
          <div className="admin-list">
            {filteredRoles.map(role => (
              <div 
                key={role.id} 
                className={`admin-list-item ${selectedRole?.id === role.id ? 'active' : ''}`}
                onClick={() => handleSelectRole(role)}
              >
                <div className="admin-list-item-name">{role.name}</div>
                {role.description && (
                  <div className="admin-list-item-details">{role.description}</div>
                )}
              </div>
            ))}
            
            {filteredRoles.length === 0 && !loading && (
              <p>No roles found.</p>
            )}
          </div>
        </div>
        
        {/* Right side - Role form */}
        <div className="admin-content">
          <form onSubmit={handleSubmit}>
            <h2>{formMode === 'create' ? 'Create New Role' : 'Edit Role'}</h2>
            
            <div className="mb-3">
              <label htmlFor="name" className="form-label">Role Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                className="form-control"
                required
              />
            </div>
            
            <div className="mb-3">
              <label htmlFor="description" className="form-label">Description</label>
              <textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                className="form-control"
                rows="3"
              ></textarea>
            </div>
            
            <div className="mb-4">
              <label className="form-label">Permissions</label>
              <div className="card">
                <div className="card-body">
                  <p className="text-muted mb-3">Select the permissions for this role:</p>
                  
                  {permissionCategories.map(category => (
                    <div key={category.name} className="mb-3">
                      <h6>{category.label}</h6>
                      <div className="d-flex flex-wrap gap-3">
                        {category.actions.map(action => (
                          <div key={`${category.name}-${action}`} className="form-check">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id={`${category.name}-${action}`}
                              checked={formData.permissions[category.name]?.includes(action) || false}
                              onChange={(e) => handlePermissionChange(category.name, action, e.target.checked)}
                            />
                            <label className="form-check-label text-capitalize" htmlFor={`${category.name}-${action}`}>
                              {action}
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            
            <div className="d-flex gap-2 mb-4">
              <button type="submit" className="btn btn-primary">
                {formMode === 'create' ? 'Create Role' : 'Update Role'}
              </button>
              
              {formMode === 'edit' && (
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteRole}
                >
                  Delete Role
                </button>
              )}
              
              <button 
                type="button" 
                className="btn btn-secondary"
                onClick={handleNewRole}
              >
                Cancel
              </button>
            </div>
          </form>
          
          {/* User management (only visible when editing a role) */}
          {selectedRole && (
            <div className="mt-4">
              <h3>Role Users</h3>
              <p>Manage users assigned to this role.</p>
              
              {/* Current users with this role */}
              {roleUsers.length > 0 && (
                <div className="mb-4">
                  <h5>Current Users</h5>
                  <div className="list-group mb-3">
                    {roleUsers.map(user => (
                      <div key={user.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{user.username || user.email}</strong>
                          {user.email && user.username && <p className="mb-0 small">{user.email}</p>}
                          {user.first_name && user.last_name && (
                            <p className="mb-0 small">{user.first_name} {user.last_name}</p>
                          )}
                        </div>
                        <button 
                          className="btn btn-sm btn-danger"
                          onClick={() => handleRemoveUser(user.id)}
                          disabled={modifyingUsers[user.id]}
                        >
                          {modifyingUsers[user.id] ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Removing...
                            </>
                          ) : 'Remove'}
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Add users section */}
              <div>
                <h5>Add Users</h5>
                <div className="mb-3">
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="form-control"
                  />
                </div>
                
                {loadingUsers ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading users...</span>
                    </div>
                    <p className="mt-2">Loading users...</p>
                  </div>
                ) : (
                  <div className="list-group">
                    {filteredUsers.map(user => (
                      <div key={user.id} className="list-group-item d-flex justify-content-between align-items-center">
                        <div>
                          <strong>{user.username || user.email}</strong>
                          {user.email && user.username && <p className="mb-0 small">{user.email}</p>}
                          {user.first_name && user.last_name && (
                            <p className="mb-0 small">{user.first_name} {user.last_name}</p>
                          )}
                        </div>
                        <button 
                          className="btn btn-sm btn-success"
                          onClick={() => handleAssignUser(user.id)}
                          disabled={modifyingUsers[user.id]}
                        >
                          {modifyingUsers[user.id] ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                              Assigning...
                            </>
                          ) : 'Assign'}
                        </button>
                      </div>
                    ))}
                    
                    {filteredUsers.length === 0 && (
                      <p className="text-center py-3">No users found or all users already have this role.</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminRoles; 